import { useState, useCallback } from 'react';
import { AI_CHAT_ATTACHMENT_CONFIG } from '@elmes/shared';

export interface AttachmentPreview {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  base64Data: string;
  previewUrl: string;
}

export interface AttachmentPayload {
  fileName: string;
  mimeType: string;
  base64Data: string;
  size: number;
}

function generateId(): string {
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (data:image/png;base64,)
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useAttachments() {
  const [attachments, setAttachments] = useState<AttachmentPreview[]>([]);
  const [error, setError] = useState<string | null>(null);

  const addFiles = useCallback(async (files: FileList | File[]) => {
    setError(null);
    const fileArr = Array.from(files);

    for (const file of fileArr) {
      // Check mime type
      if (!AI_CHAT_ATTACHMENT_CONFIG.allowedMimeTypes.includes(file.type as any)) {
        setError(`Desteklenmeyen dosya tipi: ${file.type}. Sadece JPEG, PNG, GIF, WebP desteklenir.`);
        continue;
      }
      // Check file size
      if (file.size > AI_CHAT_ATTACHMENT_CONFIG.maxFileSize) {
        setError(`Dosya çok büyük: ${file.name} (maks ${AI_CHAT_ATTACHMENT_CONFIG.maxFileSize / 1024 / 1024}MB)`);
        continue;
      }
    }

    // Check total count
    setAttachments(prev => {
      const remaining = AI_CHAT_ATTACHMENT_CONFIG.maxAttachments - prev.length;
      if (remaining <= 0) {
        setError(`Maksimum ${AI_CHAT_ATTACHMENT_CONFIG.maxAttachments} dosya eklenebilir.`);
        return prev;
      }

      const validFiles = fileArr
        .filter(f =>
          AI_CHAT_ATTACHMENT_CONFIG.allowedMimeTypes.includes(f.type as any) &&
          f.size <= AI_CHAT_ATTACHMENT_CONFIG.maxFileSize
        )
        .slice(0, remaining);

      // Process files async
      for (const file of validFiles) {
        const id = generateId();
        const previewUrl = URL.createObjectURL(file);

        readFileAsBase64(file).then(base64Data => {
          setAttachments(current => {
            const exists = current.find(a => a.id === id);
            if (exists) return current;
            return [...current, {
              id,
              fileName: file.name,
              mimeType: file.type,
              size: file.size,
              base64Data,
              previewUrl,
            }];
          });
        });
      }

      return prev;
    });
  }, []);

  const removeFile = useCallback((id: string) => {
    setAttachments(prev => {
      const att = prev.find(a => a.id === id);
      if (att) URL.revokeObjectURL(att.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  }, []);

  const clearFiles = useCallback(() => {
    setAttachments(prev => {
      for (const att of prev) URL.revokeObjectURL(att.previewUrl);
      return [];
    });
    setError(null);
  }, []);

  const getPayload = useCallback((): AttachmentPayload[] => {
    return attachments.map(a => ({
      fileName: a.fileName,
      mimeType: a.mimeType,
      base64Data: a.base64Data,
      size: a.size,
    }));
  }, [attachments]);

  return {
    attachments,
    error,
    addFiles,
    removeFile,
    clearFiles,
    getPayload,
    hasAttachments: attachments.length > 0,
  };
}
